import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, FileText, Package, Pencil, Trash2, Wrench, Zap } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { TopBar } from '@/components/layouts/TopBar'
import { LineCategoryField } from '@/components/features/categories/LineCategoryField'
import { NO_CATEGORY, type CategoryChoice } from '@/components/features/categories/category-choice'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/format'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import {
  addDirectSaleOrderLine,
  confirmDirectSaleOrder,
  deleteDirectSaleOrderLine,
  getDirectSaleOrder,
  OrderLineKinds,
  updateDirectSaleOrderLine,
  type DirectSaleOrderDto,
  type DirectSaleOrderLineDto,
} from '@/lib/api/direct-sale-order'

// product_item / service_item → titre, quantité, prix et TVA obligatoires
// note → texte libre, pas de prix ni TVA
const pricedLineFields = {
  title: z.string().min(1, 'Titre requis'),
  description: z.string().optional(),
  quantity: z.string().min(1, 'Quantité requise'),
  unitPriceWithoutTax: z.string().min(1, 'Prix requis'),
  vatPercent: z.string().min(1, 'TVA obligatoire'),
}

const lineSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(OrderLineKinds.ProductItem), ...pricedLineFields }),
  z.object({ kind: z.literal(OrderLineKinds.ServiceItem), ...pricedLineFields }),
  z.object({
    kind: z.literal(OrderLineKinds.Note),
    title: z.string().min(1, 'Titre requis'),
    description: z.string().optional(),
    quantity: z.string().optional(),
    unitPriceWithoutTax: z.string().optional(),
    vatPercent: z.string().optional(),
  }),
])

type LineFormData = z.infer<typeof lineSchema>

const VAT_OPTIONS = [
  { value: '21', label: '21%' },
  { value: '6', label: '6%' },
  { value: '0', label: '0%' },
]

function parseOptionalDecimal(v: string | undefined): number | null {
  if (!v || v.trim() === '') return null
  const n = parseFloat(v.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function parseOptionalInt(v: string | undefined): number | null {
  if (!v || v.trim() === '') return null
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

export function DirectSaleOrderEditorPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [order, setOrder] = useState<DirectSaleOrderDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingLineId, setEditingLineId] = useState<string | null>(null)
  const [isSavingLine, setIsSavingLine] = useState(false)
  const [deletingLineId, setDeletingLineId] = useState<string | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [lineCategory, setLineCategory] = useState<CategoryChoice>(NO_CATEGORY)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LineFormData>({
    resolver: zodResolver(lineSchema),
    defaultValues: {
      kind: OrderLineKinds.ProductItem,
      title: '',
      description: '',
      quantity: '',
      unitPriceWithoutTax: '',
      vatPercent: '',
    },
  })

  const kindValue = watch('kind')
  const vatPercentValue = watch('vatPercent')
  const isItemLine = kindValue !== OrderLineKinds.Note

  const loadOrder = useCallback(async () => {
    if (!projectId) return
    try {
      setError(null)
      const data = await getDirectSaleOrder(projectId)
      if (data.isLocked) {
        navigate(`/projects/${projectId}`, { replace: true })
        return
      }
      setOrder(data)
    } catch {
      setError('Erreur lors du chargement de la commande')
    } finally {
      setIsLoading(false)
    }
  }, [projectId, navigate])

  useEffect(() => {
    void loadOrder()
  }, [loadOrder])

  const defaultVatString = order?.defaultVatPercent != null ? String(order.defaultVatPercent) : '21'

  // Labels already used in this order, suggested while typing a free one
  const existingCategories = useMemo(() => {
    if (!order) return []
    return [...new Set(order.lines.map((l) => l.categoryLabel).filter(Boolean) as string[])]
  }, [order])

  const openAddDialog = (kind: typeof OrderLineKinds.ProductItem | typeof OrderLineKinds.ServiceItem | typeof OrderLineKinds.Note = OrderLineKinds.ProductItem) => {
    setEditingLineId(null)
    setLineCategory(NO_CATEGORY)
    reset({
      kind,
      title: '',
      description: '',
      quantity: kind === OrderLineKinds.Note ? '' : '1',
      unitPriceWithoutTax: '',
      vatPercent: kind === OrderLineKinds.Note ? '' : defaultVatString,
    })
    setDialogOpen(true)
  }

  const openEditDialog = (line: DirectSaleOrderLineDto) => {
    setEditingLineId(line.id)
    const lineKind = line.kind === OrderLineKinds.Note
      ? OrderLineKinds.Note
      : line.kind === OrderLineKinds.ServiceItem
        ? OrderLineKinds.ServiceItem
        : OrderLineKinds.ProductItem
    setLineCategory({ categoryId: line.categoryId ?? null, categoryLabel: line.categoryLabel ?? null })
    reset({
      kind: lineKind,
      title: line.title,
      description: line.description ?? '',
      quantity: line.quantity != null ? String(line.quantity) : '',
      unitPriceWithoutTax: line.unitPriceWithoutTax != null ? String(line.unitPriceWithoutTax) : '',
      vatPercent: line.vatPercent != null ? String(line.vatPercent) : defaultVatString,
    })
    setDialogOpen(true)
  }

  const handleSaveLine = async (data: LineFormData) => {
    if (!projectId) return
    setIsSavingLine(true)
    try {
      const isNote = data.kind === OrderLineKinds.Note
      const payload = {
        kind: data.kind,
        categoryId: lineCategory.categoryId,
        categoryLabel: lineCategory.categoryId ? null : lineCategory.categoryLabel?.trim() || null,
        title: data.title.trim(),
        description: data.description?.trim() || null,
        quantity: isNote ? null : parseOptionalDecimal(data.quantity),
        unitPriceWithoutTax: isNote ? null : parseOptionalDecimal(data.unitPriceWithoutTax),
        vatPercent: isNote ? null : parseOptionalInt(data.vatPercent),
      }

      const updated = editingLineId
        ? await updateDirectSaleOrderLine(projectId, editingLineId, payload)
        : await addDirectSaleOrderLine(projectId, payload)

      setOrder(updated)
      setDialogOpen(false)
      showSuccessToast(editingLineId ? 'Ligne modifiée' : 'Ligne ajoutée')
    } catch {
      showErrorToast('Erreur lors de la sauvegarde')
    } finally {
      setIsSavingLine(false)
    }
  }

  const handleDeleteLine = async (lineId: string) => {
    if (!projectId) return
    setDeletingLineId(lineId)
    try {
      const updated = await deleteDirectSaleOrderLine(projectId, lineId)
      setOrder(updated)
      showSuccessToast('Ligne supprimée')
    } catch {
      showErrorToast('Erreur lors de la suppression')
    } finally {
      setDeletingLineId(null)
    }
  }

  const handleConfirmOrder = async () => {
    if (!projectId || !order || order.lines.length === 0) return
    setIsConfirming(true)
    try {
      const updated = await confirmDirectSaleOrder(projectId)
      setOrder(updated)
      showSuccessToast('Commande validée')
      navigate(`/projects/${projectId}`)
    } catch {
      showErrorToast('Erreur lors de la validation de la commande')
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Modifier la commande</h1>
            <p className="text-sm text-muted-foreground">
              Ajoutez ou modifiez les lignes de la commande directe
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
            {error}
          </div>
        ) : order ? (
          <div className="space-y-4">
            {/* Lines */}
            <div className="rounded-lg border bg-card">
              {order.lines.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  Aucune ligne — ajoutez un article ci-dessous.
                </div>
              ) : (
                <div>
                  {order.lines.map((line) => (
                    <div key={line.id} className="flex items-start gap-3 p-4 border-b last:border-0">
                      <div className="flex-1 min-w-0">
                        {line.categoryLabel && (
                          <p className="text-xs text-muted-foreground">{line.categoryLabel}</p>
                        )}
                        <p className="text-sm font-medium">{line.title}</p>
                        {line.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{line.description}</p>
                        )}
                        {line.kind !== OrderLineKinds.Note && (
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                            {line.quantity != null && <span>Qté : {line.quantity}</span>}
                            {line.unitPriceWithoutTax != null && (
                              <span>PU HT : {formatCurrency(line.unitPriceWithoutTax)}</span>
                            )}
                            {line.discountPercent != null && line.discountPercent > 0 && (
                              <span>Rem. : {Math.round(line.discountPercent * 100)}%</span>
                            )}
                            {line.vatPercent != null && <span>TVA : {line.vatPercent}%</span>}
                          </div>
                        )}
                      </div>
                      <div className="text-sm font-medium shrink-0">
                        {line.kind !== OrderLineKinds.Note && line.lineTotalWithTax != null
                          ? formatCurrency(line.lineTotalWithTax)
                          : '—'}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(line)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteLine(line.id)}
                          disabled={deletingLineId === line.id}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totals */}
            {order.lines.some(l => l.kind !== OrderLineKinds.Note) && (
              <div className="rounded-lg border bg-card p-4 space-y-1.5">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Total HTVA</span>
                  <span>{formatCurrency(order.totalWithoutTax)}</span>
                </div>
                {order.vatBreakdown.map((vb) => (
                  <div key={vb.vatPercent} className="flex justify-between text-xs text-muted-foreground">
                    <span>TVA {vb.vatPercent}%</span>
                    <span>{formatCurrency(vb.taxAmount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold pt-1 border-t">
                  <span>Total TTC</span>
                  <span>{formatCurrency(order.totalWithTax)}</span>
                </div>
              </div>
            )}

            {/* Add line buttons */}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" data-testid="direct-sale-add-product-btn" onClick={() => openAddDialog(OrderLineKinds.ProductItem)} className="gap-2">
                <Package className="h-4 w-4" />
                Ajouter élément
              </Button>
              <Button variant="outline" data-testid="direct-sale-add-service-btn" onClick={() => openAddDialog(OrderLineKinds.ServiceItem)} className="gap-2">
                <Wrench className="h-4 w-4" />
                Ajouter service
              </Button>
              <Button variant="outline" data-testid="direct-sale-add-note-btn" onClick={() => openAddDialog(OrderLineKinds.Note)} className="gap-2">
                <FileText className="h-4 w-4" />
                Ajouter note
              </Button>
            </div>

            {/* Confirm order + Back */}
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)}>
                ← Retour au projet
              </Button>
              {order.status === 'draft' && (
                <Button
                  data-testid="direct-sale-confirm-order-btn"
                  onClick={handleConfirmOrder}
                  disabled={isConfirming || order.lines.length === 0}
                  className="gap-1.5"
                >
                  <Zap className="h-3.5 w-3.5" />
                  {isConfirming ? 'Validation…' : 'Valider la commande'}
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Add / Edit line dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>{editingLineId ? 'Modifier la ligne' : 'Ajouter une ligne'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(handleSaveLine)} className="space-y-4">
            {/* Kind indicator (read-only, set by button) */}
            {editingLineId && (
              <div className="space-y-1.5">
                <Label>Type de ligne</Label>
                <Select
                  value={kindValue}
                  onValueChange={(v) => {
                    setValue('kind', v as typeof OrderLineKinds.ProductItem | typeof OrderLineKinds.ServiceItem | typeof OrderLineKinds.Note)
                    if (v !== OrderLineKinds.Note && !vatPercentValue) {
                      setValue('vatPercent', defaultVatString)
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={OrderLineKinds.ProductItem}>Produit physique</SelectItem>
                    <SelectItem value={OrderLineKinds.ServiceItem}>Prestation de service</SelectItem>
                    <SelectItem value={OrderLineKinds.Note}>Note (texte libre)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="line-title">Titre *</Label>
              <Input
                id="line-title"
                data-testid="direct-sale-line-title-input"
                placeholder={isItemLine ? 'Ex: Fourniture et pose cuisine' : 'Ex: Remarque importante'}
                {...register('title')}
              />
              {errors.title && (
                <p className="text-xs text-destructive">{errors.title.message}</p>
              )}
            </div>

            {isItemLine && (
              <div className="space-y-1.5">
                <Label htmlFor="line-category">Catégorie</Label>
                {projectId && (
                  <LineCategoryField
                    id="line-category"
                    projectId={projectId}
                    value={lineCategory}
                    onChange={setLineCategory}
                    suggestions={existingCategories}
                    placeholder="Ex: Cuisine, Salle de bain…"
                  />
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="line-description">Description</Label>
              <Textarea
                id="line-description"
                placeholder="Détails optionnels"
                rows={3}
                {...register('description')}
              />
            </div>

            {isItemLine && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="line-quantity">Quantité *</Label>
                    <Input
                      id="line-quantity"
                      data-testid="direct-sale-line-quantity-input"
                      type="number"
                      placeholder="1"
                      min="0"
                      step="any"
                      {...register('quantity')}
                    />
                    {'quantity' in errors && errors.quantity && (
                      <p className="text-xs text-destructive">{errors.quantity.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="line-price">Prix unitaire HT *</Label>
                    <Input
                      id="line-price"
                      data-testid="direct-sale-line-price-input"
                      type="number"
                      placeholder="0.00"
                      step="any"
                      {...register('unitPriceWithoutTax')}
                    />
                    {'unitPriceWithoutTax' in errors && errors.unitPriceWithoutTax && (
                      <p className="text-xs text-destructive">{errors.unitPriceWithoutTax.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>TVA *</Label>
                  <Select
                    value={vatPercentValue ?? ''}
                    onValueChange={(v) => setValue('vatPercent', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {VAT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {'vatPercent' in errors && errors.vatPercent && (
                    <p className="text-xs text-destructive">{errors.vatPercent.message}</p>
                  )}
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="direct-sale-line-submit-btn" disabled={isSavingLine}>
                {isSavingLine ? 'Enregistrement…' : editingLineId ? 'Modifier' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
